import { useMemo } from 'react';
import { Combobox } from '@/components/ui';
import { listHrPeople, type HrPersonRef } from '@/api/services/hrWorkspace.service';

const SEPARATOR = '|';

/**
 * Finds a person across companies with a server-side search, so it works past a
 * single page of staff. Choosing the person also settles the company, because
 * every HR record belongs to one company's employment. Someone in two companies
 * appears once for each.
 */
export function EmployeePicker({
  id,
  label = 'Employee',
  companyIds,
  value,
  onChange,
  showCompany = true,
}: {
  id: string;
  label?: string;
  companyIds?: string[];
  value: HrPersonRef | null;
  onChange: (person: HrPersonRef | null) => void;
  showCompany?: boolean;
}): React.ReactElement {
  const loadOptions = useMemo(
    () => async (query: string) => {
      const page = await listHrPeople(companyIds, 1, 20, {
        setUpOnly: true,
        ...(query.trim() && { search: query.trim() }),
      });
      return (Array.isArray(page?.data) ? page.data : []).map((row) => {
        const name = row.employee
          ? `${row.employee.firstName} ${row.employee.lastName}`.trim()
          : 'Unnamed employee';
        return {
          value: `${row.companyId}${SEPARATOR}${row.userId}`,
          label: [name, row.employee?.employeeId, showCompany ? row.companyName : null]
            .filter(Boolean)
            .join(' · '),
        };
      });
    },
    [companyIds, showCompany]
  );
  return (
    <Combobox
      id={id}
      label={label}
      placeholder="Search by name or employee ID"
      value={value ? `${value.companyId}${SEPARATOR}${value.userId}` : ''}
      onChange={(next) => {
        const [companyId, userId] = next.split(SEPARATOR);
        onChange(companyId && userId ? { companyId, userId } : null);
      }}
      loadOptions={loadOptions}
    />
  );
}
